import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Home,
  Search,
  Key,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Save,
  X,
  ArrowLeft,
  LogOut,
  Filter,
  MessageSquare,
  MapPin,
  Info,
  ChevronDown,
  ChevronUp,
  List,
  Hash,
  Plus,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyTracking = () => {
  const { user, logout, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // View mode: "properties" (par logement, existant) OU "keys" (par N° de clés)
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('propTrackingViewMode') || 'properties'; }
    catch { return 'properties'; }
  });
  const [maxKeyNumber, setMaxKeyNumber] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('propTrackingMaxKey') || '150', 10);
      return Number.isFinite(v) && v > 0 ? v : 150;
    } catch { return 150; }
  });
  // Sélection courante d'assignation pour chaque N° libre (map keyNumber → propertyId)
  const [pendingAssign, setPendingAssign] = useState({});
  const [assigningKey, setAssigningKey] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('propTrackingViewMode', viewMode); } catch (_e) { /* ignore */ }
  }, [viewMode]);
  useEffect(() => {
    try { localStorage.setItem('propTrackingMaxKey', String(maxKeyNumber)); } catch (_e) { /* ignore */ }
  }, [maxKeyNumber]);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/properties/tracking/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const startEditing = (property) => {
    setEditingId(property.id);
    setExpandedId(property.id);
    setEditData({
      key_number: property.key_number || '',
      keybox_code: property.keybox_code || '',
      keybox_address: property.keybox_address || '',
      keybox_position: property.keybox_position || '',
      building_code: property.building_code || '',
      access_instructions: property.access_instructions || '',
      status: property.status || 'actif',
      termination_date: property.termination_date || '',
      tracking_notes: property.tracking_notes || ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({});
  };

  const toggleExpand = (propertyId) => {
    setExpandedId(expandedId === propertyId ? null : propertyId);
  };

  const hasAccessInfo = (property) => {
    return property.keybox_address || property.keybox_position || property.access_instructions;
  };

  const saveChanges = async (propertyId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/properties/${propertyId}/tracking`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const updated = await response.json();
        setProperties(prev => prev.map(p => p.id === propertyId ? updated : p));
        setEditingId(null);
        setEditData({});
      } else {
        alert('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const assignKeyNumber = async (propertyId, keyNumber) => {
    setAssigningKey(keyNumber);
    try {
      const response = await fetch(`${API_URL}/api/properties/${propertyId}/tracking`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key_number: String(keyNumber) })
      });
      if (response.ok) {
        const updated = await response.json();
        setProperties(prev => prev.map(p => p.id === propertyId ? updated : p));
        setPendingAssign(prev => {
          const next = { ...prev };
          delete next[keyNumber];
          return next;
        });
      } else {
        alert('Erreur lors de l\'assignation');
      }
    } catch (error) {
      console.error('Error assigning key:', error);
      alert('Erreur lors de l\'assignation');
    } finally {
      setAssigningKey(null);
    }
  };

  const unassignKeyNumber = async (propertyId) => {
    if (!window.confirm('Retirer ce numéro de clé de ce logement ?')) return;
    try {
      const response = await fetch(`${API_URL}/api/properties/${propertyId}/tracking`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key_number: '' })
      });
      if (response.ok) {
        const updated = await response.json();
        setProperties(prev => prev.map(p => p.id === propertyId ? updated : p));
      } else {
        alert('Erreur lors du retrait');
      }
    } catch (error) {
      console.error('Error unassigning key:', error);
      alert('Erreur lors du retrait');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      actif: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Actif' },
      en_cours: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'En cours' },
      resiliation: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Résiliation' }
    };
    const cfg = config[status] || config.actif;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} border-0 flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  const filteredProperties = properties.filter(p => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        p.name?.toLowerCase().includes(query) ||
        p.city?.toLowerCase().includes(query) ||
        p.key_number?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // ============ Vue "Par N° de clés" ============
  // Regroupe les logements par n° de clé (parsé en entier)
  const parseKeyNum = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    if (!s) return null;
    const m = s.match(/^(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  };

  const propsByKey = new Map(); // key int → array of properties
  const propsWithoutKey = [];
  const propsWithNonNumericKey = [];
  properties.forEach(p => {
    const n = parseKeyNum(p.key_number);
    if (n == null) {
      if (p.key_number && String(p.key_number).trim()) {
        propsWithNonNumericKey.push(p);
      } else {
        propsWithoutKey.push(p);
      }
    } else {
      if (!propsByKey.has(n)) propsByKey.set(n, []);
      propsByKey.get(n).push(p);
    }
  });

  // Slots à afficher : 1..maxKeyNumber PLUS toute clé au-delà déjà attribuée
  const highestAssigned = propsByKey.size > 0 ? Math.max(...propsByKey.keys()) : 0;
  const totalSlots = Math.max(maxKeyNumber, highestAssigned);

  // Recherche : filtre les slots (soit par n° soit par nom du logement associé)
  const matchesKeySearch = (num, props) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (String(num).includes(q)) return true;
    return props.some(p =>
      p.name?.toLowerCase().includes(q) ||
      p.city?.toLowerCase().includes(q)
    );
  };
  const showEmptyOnFilter = searchQuery ? searchQuery : null;

  // Propriétés sans n° (candidates à l'assignation)
  const assignablePool = [...propsWithoutKey].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Gouvernant has its own header, Admin uses Sidebar
  const isGouvernant = user?.role === 'gouvernant';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 ${!isGouvernant ? 'flex' : ''}`}>
      {!isGouvernant && <Sidebar />}
      
      {/* Gouvernant Header */}
      {isGouvernant && (
        <header className="bg-[#003366] text-white px-4 py-4 shadow-lg sticky top-0 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={() => navigate('/gouvernant')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">Suivi des logements</h1>
                <p className="text-blue-200 text-sm">{user?.name}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>
      )}

      <main className={`${!isGouvernant ? 'flex-1 overflow-auto ml-0 md:ml-72' : 'max-w-7xl mx-auto'} p-4 md:p-8`}>
        {/* Header for Admin */}
        {!isGouvernant && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h1 className="text-2xl font-bold text-[#003366] flex items-center gap-2">
              <Home className="h-6 w-6" />
              Suivi des logements
            </h1>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-6">
          <CardContent className="p-4">
            {/* View mode toggle */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-slate-500 mr-2">Vue :</span>
              <Button
                size="sm"
                variant={viewMode === 'properties' ? 'default' : 'outline'}
                className={viewMode === 'properties' ? 'bg-[#003366]' : ''}
                onClick={() => setViewMode('properties')}
                data-testid="view-by-properties-btn"
              >
                <List className="h-4 w-4 mr-1" />
                Par logement
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'keys' ? 'default' : 'outline'}
                className={viewMode === 'keys' ? 'bg-[#003366]' : ''}
                onClick={() => setViewMode('keys')}
                data-testid="view-by-keys-btn"
              >
                <Hash className="h-4 w-4 mr-1" />
                Par N° de clés
              </Button>
              {viewMode === 'keys' && (
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-slate-500">Afficher jusqu'à N°</span>
                  <Input
                    type="number"
                    min="1"
                    max="9999"
                    value={maxKeyNumber}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isFinite(v) && v > 0) setMaxKeyNumber(v);
                    }}
                    className="w-20 h-8 text-sm"
                    data-testid="max-key-number-input"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom, ville ou n° de clés..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-properties"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]" data-testid="filter-status">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="en_cours">En cours</SelectItem>
                  <SelectItem value="resiliation">Résiliation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {properties.filter(p => p.status === 'actif' || !p.status).length}
              </p>
              <p className="text-sm text-slate-500">Actifs</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {properties.filter(p => p.status === 'en_cours').length}
              </p>
              <p className="text-sm text-slate-500">En cours</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">
                {properties.filter(p => p.status === 'resiliation').length}
              </p>
              <p className="text-sm text-slate-500">Résiliation</p>
            </CardContent>
          </Card>
        </div>

        {/* Properties Table (view "Par logement") */}
        {viewMode === 'properties' && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              {filteredProperties.length} logement{filteredProperties.length > 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-[#003366] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-500">Chargement...</p>
              </div>
            ) : filteredProperties.length === 0 ? (
              <p className="text-center py-12 text-slate-500">Aucun logement trouvé</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Logement</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">N° Clés</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Code boîte</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Code immeuble</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Statut</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProperties.map(property => (
                      <React.Fragment key={property.id}>
                        <tr 
                          className={`border-b border-slate-100 hover:bg-slate-50 ${
                            editingId === property.id ? 'bg-blue-50 border-b-0' : ''
                          } ${property.tracking_notes && editingId !== property.id ? 'border-b-0' : ''}`}
                        >
                          {/* Logement */}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: property.color || '#003366' }}
                              />
                              <div>
                                <p className="font-medium text-slate-800">{property.name}</p>
                                <p className="text-xs text-slate-500">{property.city}</p>
                              </div>
                            </div>
                          </td>

                          {/* N° Clés */}
                          <td className="py-3 px-4">
                            {editingId === property.id ? (
                              <Input
                                value={editData.key_number}
                                onChange={(e) => setEditData({ ...editData, key_number: e.target.value })}
                                placeholder="N° clés"
                                className="w-24 h-8 text-sm"
                              />
                            ) : (
                              <span className={`text-sm font-medium ${property.key_number ? 'text-slate-700' : 'text-slate-400'}`}>
                                {property.key_number || '-'}
                              </span>
                            )}
                          </td>

                          {/* Code boîte */}
                          <td className="py-3 px-4">
                            {editingId === property.id ? (
                              <Input
                                value={editData.keybox_code}
                                onChange={(e) => setEditData({ ...editData, keybox_code: e.target.value })}
                                placeholder="Code"
                                className="w-24 h-8 text-sm"
                              />
                            ) : (
                              <div className="flex items-center gap-1">
                                <Key className="h-3 w-3 text-slate-400" />
                                <span className="text-sm text-slate-600 font-mono">
                                  {property.keybox_code || '-'}
                                </span>
                              </div>
                            )}
                          </td>

                          {/* Code immeuble */}
                          <td className="py-3 px-4">
                            {editingId === property.id ? (
                              <Input
                                value={editData.building_code}
                                onChange={(e) => setEditData({ ...editData, building_code: e.target.value })}
                                placeholder="Code"
                                className="w-24 h-8 text-sm"
                              />
                            ) : (
                              <div className="flex items-center gap-1">
                                <Building className="h-3 w-3 text-slate-400" />
                                <span className="text-sm text-slate-600 font-mono">
                                  {property.building_code || '-'}
                                </span>
                              </div>
                            )}
                          </td>

                          {/* Statut */}
                          <td className="py-3 px-4">
                            {editingId === property.id ? (
                              <div className="space-y-2">
                                <Select 
                                  value={editData.status} 
                                  onValueChange={(v) => setEditData({ ...editData, status: v })}
                                >
                                  <SelectTrigger className="w-32 h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="actif">Actif</SelectItem>
                                    <SelectItem value="en_cours">En cours</SelectItem>
                                    <SelectItem value="resiliation">Résiliation</SelectItem>
                                  </SelectContent>
                                </Select>
                                {editData.status === 'resiliation' && (
                                  <Input
                                    type="date"
                                    value={editData.termination_date}
                                    onChange={(e) => setEditData({ ...editData, termination_date: e.target.value })}
                                    className="w-36 h-8 text-sm"
                                  />
                                )}
                              </div>
                            ) : (
                              <div>
                                {getStatusBadge(property.status || 'actif')}
                                {property.status === 'resiliation' && property.termination_date && (
                                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {format(new Date(property.termination_date), 'd MMM yyyy', { locale: fr })}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4">
                            {editingId === property.id ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => saveChanges(property.id)}
                                  disabled={saving}
                                  className="bg-emerald-500 hover:bg-emerald-600 h-8"
                                >
                                  {saving ? (
                                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                  ) : (
                                    <Save className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEditing}
                                  className="h-8"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditing(property)}
                                  className="h-8"
                                  data-testid={`edit-${property.id}`}
                                >
                                  Modifier
                                </Button>
                                {hasAccessInfo(property) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleExpand(property.id)}
                                    className="h-8 px-2"
                                  >
                                    {expandedId === property.id ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>

                        {/* Section Instructions d'accès - visible en édition ou si expandée */}
                        {(editingId === property.id || expandedId === property.id) && (
                          <tr className={`border-b-0 ${editingId === property.id ? 'bg-blue-50' : 'bg-amber-50/50'}`}>
                            <td colSpan={6} className="py-3 px-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Info className="h-4 w-4 text-amber-600" />
                                <span className="text-sm font-semibold text-amber-700">Instructions d'accès</span>
                              </div>
                              
                              {editingId === property.id ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* Adresse boîte à clés */}
                                  <div>
                                    <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      Adresse boîte à clés (si différente)
                                    </label>
                                    <Input
                                      value={editData.keybox_address}
                                      onChange={(e) => setEditData({ ...editData, keybox_address: e.target.value })}
                                      placeholder="Ex: 12 rue du Port"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  
                                  {/* Position sur façade */}
                                  <div>
                                    <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                                      <Key className="h-3 w-3" />
                                      N° boîte sur façade
                                    </label>
                                    <Input
                                      value={editData.keybox_position}
                                      onChange={(e) => setEditData({ ...editData, keybox_position: e.target.value })}
                                      placeholder="Ex: 3ème en partant de la gauche"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  
                                  {/* Instructions détaillées */}
                                  <div className="md:col-span-1">
                                    <label className="text-xs text-slate-500 mb-1 block">
                                      Instructions supplémentaires
                                    </label>
                                    <Input
                                      value={editData.access_instructions}
                                      onChange={(e) => setEditData({ ...editData, access_instructions: e.target.value })}
                                      placeholder="Ex: Sonner au gardien, clé sous le pot..."
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                  {property.keybox_address && (
                                    <div>
                                      <span className="text-slate-500">Adresse boîte: </span>
                                      <span className="text-slate-700 font-medium">{property.keybox_address}</span>
                                    </div>
                                  )}
                                  {property.keybox_position && (
                                    <div>
                                      <span className="text-slate-500">Position: </span>
                                      <span className="text-slate-700 font-medium">{property.keybox_position}</span>
                                    </div>
                                  )}
                                  {property.access_instructions && (
                                    <div>
                                      <span className="text-slate-500">Instructions: </span>
                                      <span className="text-slate-700">{property.access_instructions}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        
                        {/* Ligne de commentaires - visible en édition ou si commentaire existe */}
                        {(editingId === property.id || property.tracking_notes) && (
                          <tr className={`border-b border-slate-100 ${editingId === property.id ? 'bg-blue-50' : 'bg-slate-50/50'}`}>
                            <td colSpan={6} className="py-2 px-4">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="h-4 w-4 text-slate-400 mt-1 flex-shrink-0" />
                                {editingId === property.id ? (
                                  <Input
                                    value={editData.tracking_notes}
                                    onChange={(e) => setEditData({ ...editData, tracking_notes: e.target.value })}
                                    placeholder="Commentaires (ex: changement de serrure prévu, travaux en cours...)"
                                    className="flex-1 h-8 text-sm"
                                  />
                                ) : (
                                  <p className="text-sm text-slate-600 italic">{property.tracking_notes}</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Vue "Par N° de clés" */}
        {viewMode === 'keys' && (
          <Card className="border-0 shadow-lg" data-testid="keys-view-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Hash className="h-5 w-5 text-[#003366]" />
                Liste par numéro de clé (1 → {totalSlots})
              </CardTitle>
              <div className="text-xs text-slate-500">
                {propsByKey.size} attribuée{propsByKey.size > 1 ? 's' : ''} · {Math.max(0, totalSlots - propsByKey.size)} libre{Math.max(0, totalSlots - propsByKey.size) > 1 ? 's' : ''} · {propsWithoutKey.length} sans n°
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-[#003366] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-slate-500">Chargement...</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-600 w-20">N°</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-600">Logement</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-600">Ville</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-600">Statut</th>
                          <th className="text-left py-2 px-3 text-sm font-semibold text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: totalSlots }, (_, i) => i + 1).map(num => {
                          const props = propsByKey.get(num) || [];
                          if (!matchesKeySearch(num, props)) return null;
                          const isEmpty = props.length === 0;

                          if (isEmpty) {
                            // Ligne libre : n'affiche que si pas de filtre statut spécifique
                            if (filterStatus !== 'all') return null;
                            return (
                              <tr
                                key={`slot-${num}`}
                                className="border-b border-slate-100 hover:bg-blue-50/40 transition"
                                data-testid={`key-row-${num}`}
                              >
                                <td className="py-2 px-3">
                                  <span className="inline-flex items-center justify-center min-w-[2.5rem] h-8 px-2 rounded-md bg-slate-100 text-slate-500 font-mono font-semibold text-sm">
                                    {num}
                                  </span>
                                </td>
                                <td className="py-2 px-3" colSpan={3}>
                                  <span className="text-sm text-slate-400 italic">— Libre —</span>
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    <Select
                                      value={pendingAssign[num] || ''}
                                      onValueChange={(v) => setPendingAssign(prev => ({ ...prev, [num]: v }))}
                                    >
                                      <SelectTrigger className="w-56 h-8 text-sm" data-testid={`assign-select-${num}`}>
                                        <SelectValue placeholder="Assigner à un logement…" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {assignablePool.length === 0 ? (
                                          <SelectItem value="__none__" disabled>
                                            Tous les logements ont un n°
                                          </SelectItem>
                                        ) : (
                                          assignablePool.map(p => (
                                            <SelectItem key={p.id} value={p.id}>
                                              {p.name}{p.city ? ` — ${p.city}` : ''}
                                            </SelectItem>
                                          ))
                                        )}
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      size="sm"
                                      onClick={() => pendingAssign[num] && assignKeyNumber(pendingAssign[num], num)}
                                      disabled={!pendingAssign[num] || assigningKey === num}
                                      className="h-8 bg-emerald-500 hover:bg-emerald-600"
                                      data-testid={`assign-btn-${num}`}
                                    >
                                      {assigningKey === num ? (
                                        <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                                      ) : (
                                        <>
                                          <Plus className="h-3 w-3 mr-1" />
                                          Assigner
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          // Ligne(s) attribuée(s) à ce n°
                          return props.map((property, idx) => {
                            if (filterStatus !== 'all' && property.status !== filterStatus) return null;
                            return (
                              <tr
                                key={`slot-${num}-${property.id}`}
                                className="border-b border-slate-100 hover:bg-slate-50"
                                data-testid={`key-row-${num}`}
                              >
                                <td className="py-2 px-3">
                                  {idx === 0 ? (
                                    <span className="inline-flex items-center justify-center min-w-[2.5rem] h-8 px-2 rounded-md bg-[#003366] text-white font-mono font-semibold text-sm">
                                      {num}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center justify-center min-w-[2.5rem] h-8 px-2 rounded-md bg-amber-100 text-amber-700 font-mono text-xs" title="Doublon de n°">
                                      {num}·
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: property.color || '#003366' }}
                                    />
                                    <span className="font-medium text-slate-800 text-sm">{property.name}</span>
                                  </div>
                                </td>
                                <td className="py-2 px-3 text-sm text-slate-500">
                                  {property.city || '-'}
                                </td>
                                <td className="py-2 px-3">
                                  {getStatusBadge(property.status || 'actif')}
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setViewMode('properties');
                                        setTimeout(() => startEditing(property), 60);
                                      }}
                                      className="h-8"
                                      data-testid={`key-edit-${property.id}`}
                                    >
                                      <ExternalLink className="h-3 w-3 mr-1" />
                                      Modifier
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => unassignKeyNumber(property.id)}
                                      className="h-8 text-red-600 hover:bg-red-50"
                                      data-testid={`key-unassign-${property.id}`}
                                      title="Retirer ce n°"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Autres logements (n° non numérique OU sans n°) */}
                  {(propsWithNonNumericKey.length > 0 || propsWithoutKey.length > 0) && !showEmptyOnFilter && (
                    <div className="mt-6 border-t pt-4">
                      {propsWithNonNumericKey.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
                            N° non standard ({propsWithNonNumericKey.length})
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {propsWithNonNumericKey.map(p => (
                              <div key={p.id} className="flex items-center gap-2 p-2 border rounded-md bg-amber-50/40">
                                <span className="font-mono text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                  {p.key_number}
                                </span>
                                <span className="text-sm text-slate-700 truncate flex-1">{p.name}</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  onClick={() => {
                                    setViewMode('properties');
                                    setTimeout(() => startEditing(p), 60);
                                  }}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {propsWithoutKey.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                            Sans numéro de clé ({propsWithoutKey.length})
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {propsWithoutKey.map(p => (
                              <div key={p.id} className="flex items-center gap-2 p-2 border rounded-md bg-slate-50">
                                <span className="font-mono text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded">
                                  —
                                </span>
                                <span className="text-sm text-slate-700 truncate flex-1">{p.name}</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  onClick={() => {
                                    setViewMode('properties');
                                    setTimeout(() => startEditing(p), 60);
                                  }}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PropertyTracking;
